// Pilot candidate scoring worksheet — pilot-cohort.md §2.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type PilotCandidate, type RosterRow } from "../lib/api";

const FUNCTIONS = ["Engineering","Sales","HR","Finance","Project Management","Operations","Other"];
const SENIORITIES = ["Lead","Sr IC","Jr"];
const SENTIMENTS = ["Enthusiast","Neutral","Skeptic"];

export function PilotCandidates() {
  const qc = useQueryClient();
  const { data } = useQuery<PilotCandidate[]>({ queryKey: ["pilot-candidates"], queryFn: api.pilotCandidates.list });
  const roster = useQuery<RosterRow[]>({ queryKey: ["roster"], queryFn: api.roster.list });

  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    candidate_id: "", function: "Engineering", seniority: "Sr IC", sentiment: "Enthusiast",
    c1_reps: 0, c2_visible: 0, c3_honest: 0, c4_tier3: 0, c5_mentor: 0,
    excluded: false, exclusion_reason: "", notes: "",
  });

  const create = useMutation({
    mutationFn: () => api.pilotCandidates.create(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pilot-candidates"] });
      setAdding(false);
    },
  });

  const score = (form.c1_reps + form.c2_visible + form.c3_honest + form.c4_tier3 + form.c5_mentor);
  const shortlist = score >= 3 && !form.excluded;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Pilot candidates</h1>
        <button className="px-3 py-1 rounded bg-blue-600 text-white text-sm" onClick={() => setAdding((s) => !s)}>
          {adding ? "Cancel" : "Score candidate"}
        </button>
      </div>

      {adding ? (
        <form className="border rounded p-3 bg-white space-y-3" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-slate-600">Candidate</label>
              <select className="w-full border rounded px-2 py-1 text-sm" value={form.candidate_id} onChange={(e) => setForm({ ...form, candidate_id: e.target.value })} required>
                <option value="">— pick —</option>
                {(roster.data ?? []).map((r) => <option key={r.id} value={r.id}>{r.name} ({r.function})</option>)}
              </select>
            </div>
            <SelectField label="Function" value={form.function} options={FUNCTIONS} onChange={(v) => setForm({ ...form, function: v })} />
            <SelectField label="Seniority" value={form.seniority} options={SENIORITIES} onChange={(v) => setForm({ ...form, seniority: v })} />
            <SelectField label="Sentiment" value={form.sentiment} options={SENTIMENTS} onChange={(v) => setForm({ ...form, sentiment: v })} />
          </div>

          <fieldset className="space-y-1">
            <legend className="text-sm font-medium">Scoring criteria (1 = yes, 0 = no)</legend>
            {([
              ["c1_reps",    "Reps: has a repetitive workflow plausibly automatable"],
              ["c2_visible", "Visible: wins observable by peers, not buried"],
              ["c3_honest",  "Honest reporter: will log a 'no-win' week"],
              ["c4_tier3",   "Tier-3 exposure: routinely handles sensitive data"],
              ["c5_mentor",  "Mentor potential: could grow into a Champion"],
            ] as const).map(([k, label]) => (
              <label key={k} className="block text-sm">
                <input type="checkbox"
                  className="mr-2"
                  checked={(form as any)[k] === 1}
                  onChange={(e) => setForm({ ...form, [k]: e.target.checked ? 1 : 0 })}
                />
                {label}
              </label>
            ))}
          </fieldset>

          <div className="text-sm">
            Score: <b>{score}/5</b> · {shortlist ? <span className="text-emerald-700">Shortlist ✓</span> : <span className="text-slate-500">Below threshold</span>}
          </div>

          <label className="block text-sm">
            <input type="checkbox" className="mr-2" checked={form.excluded} onChange={(e) => setForm({ ...form, excluded: e.target.checked })}/>
            Excluded (overrides score)
          </label>
          {form.excluded ? (
            <input className="w-full border rounded px-2 py-1 text-sm" placeholder="Exclusion reason"
              value={form.exclusion_reason} onChange={(e) => setForm({ ...form, exclusion_reason: e.target.value })} />
          ) : null}

          <textarea rows={2} className="w-full border rounded px-2 py-1 text-sm" placeholder="Notes"
            value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />

          {create.error ? <p className="text-red-700 text-sm">{(create.error as Error).message}</p> : null}
          <button className="px-3 py-1 rounded bg-blue-600 text-white text-sm">Save</button>
        </form>
      ) : null}

      <table className="w-full text-sm">
        <thead className="border-b border-slate-300 text-left">
          <tr>
            <th className="py-2">Candidate</th>
            <th>Function</th>
            <th>Seniority</th>
            <th>Sentiment</th>
            <th className="text-center">C1</th>
            <th className="text-center">C2</th>
            <th className="text-center">C3</th>
            <th className="text-center">C4</th>
            <th className="text-center">C5</th>
            <th className="text-right">Score</th>
            <th>Shortlist?</th>
          </tr>
        </thead>
        <tbody>
          {(data ?? []).map((c) => (
            <tr key={c.id} className={`border-b border-slate-100 ${c.shortlist ? "bg-emerald-50" : ""}`}>
              <td className="py-2">{c.candidate_name}</td>
              <td>{c.function}</td>
              <td>{c.seniority}</td>
              <td>{c.sentiment}</td>
              {[c.c1_reps, c.c2_visible, c.c3_honest, c.c4_tier3, c.c5_mentor].map((v, i) => (
                <td key={i} className="text-center">{v}</td>
              ))}
              <td className="text-right font-medium">{c.score}/5</td>
              <td>{c.shortlist ? <span className="text-emerald-700">✓</span> : c.excluded ? <span className="text-rose-700">excl</span> : <span className="text-slate-400">—</span>}</td>
            </tr>
          ))}
          {(data ?? []).length === 0 ? (
            <tr><td colSpan={11} className="py-4 text-center text-slate-500">No candidates scored yet.</td></tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function SelectField({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="text-xs text-slate-600">{label}</label>
      <select className="w-full border rounded px-2 py-1 text-sm" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}
